import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { AdminTable } from "../components/admins/AdminTable";
import { AddAdminDialog } from "../components/admins/AddAdminDialog";
import { EditAdminDialog } from "../components/admins/EditAdminDialog";
import { DeleteAdminDialog } from "../components/admins/DeleteAdminDialog";
import { ExportAdminsDialog } from "../components/admins/ExportAdminsDialog";
import { Button } from "../components/ui/Button";
import { userService, User } from "../services/userService";
import { useAuth } from "../contexts/AuthContext";
import { PermissionDenied } from "../components/ui/PermissionDenied";
import { Download } from "lucide-react";

export default function AdminsPage() {
  const { hasPermission } = useAuth();
  const [admins, setAdmins] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Permission check - only users with admin:read can access this page
  if (!hasPermission('admin:read')) {
    return (
      <DashboardLayout>
        <PermissionDenied
          requiredPermission="admin:read"
          message="You don't have permission to view admins. Only users with 'admin:read' permission can access this page."
        />
      </DashboardLayout>
    );
  }

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<User | null>(null);

  const fetchAdmins = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await userService.getAllUsers({ role: "admin" });
      setAdmins(response.users);
    } catch (err) {
      console.error("Failed to fetch admins:", err);
      setError("Failed to load admins. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleEditClick = (admin: User) => {
    setSelectedAdmin(admin);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (admin: User) => {
    setSelectedAdmin(admin);
    setIsDeleteModalOpen(true);
  };

  return (
    <DashboardLayout>
      {error ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <p className="text-red-500 text-lg">{error}</p>
          <Button onClick={fetchAdmins} variant="outline" className="mt-4">
            Try Again
          </Button>
        </div>
      ) : (
        <div className="p-3 sm:p-6">
          <AdminTable
            admins={admins}
            description="Manage system administrators and their permissions."
            isLoading={isLoading}
            onAddClick={() => setIsAddModalOpen(true)}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            extraHeaderActions={
              hasPermission("analytics:export") ? (
                <Button
                  variant="outline"
                  size="lg"
                  className="whitespace-nowrap gap-2 w-full sm:w-auto h-[48px] sm:h-[44px] justify-center"
                  onClick={() => setIsExportOpen(true)}
                >
                  <Download size={16} />
                  Export CSV
                </Button>
              ) : undefined
            }
          />

          <AddAdminDialog
            isOpen={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={fetchAdmins}
          />

          <EditAdminDialog
            isOpen={isEditModalOpen}
            admin={selectedAdmin}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedAdmin(null);
            }}
            onSuccess={fetchAdmins}
          />

          <DeleteAdminDialog
            isOpen={isDeleteModalOpen}
            admin={selectedAdmin}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedAdmin(null);
            }}
            onSuccess={fetchAdmins}
          />

          <ExportAdminsDialog
            isOpen={isExportOpen}
            onClose={() => setIsExportOpen(false)}
          />
        </div>
      )}
    </DashboardLayout>
  );
}
